import React, { useCallback, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import PaqueryTable from '@paquery-team/lib-table';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import {
  DEPARTURE_TYPE_OPTIONS,
  DEPARTURE_TYPE_NAME_OVERRIDES,
  SELECTABLE_DEPARTURE_TYPE_OPTIONS,
} from 'constants/options';
import { TableDateTimeFormat } from 'constants/dateFormats';
import usePaquerypoints from 'redux/paquerypoint';
import {
  paquerypointsLoadedSelector,
  paquerypointsObjectItemSelector,
} from 'redux/paquerypoint/selectors';
import useLogisticOperators from 'redux/logisticOperator';
import {
  logisticOperatorsLoadedSelector,
  logisticOperatorsObjectItemSelector,
} from 'redux/logisticOperator/selectors';
import { dispatchTypesGlobalsSelector } from 'redux/globals/selectors';
import { authProfile } from 'redux/auth/selectors';
import { isOplRole } from 'constants/roles';
import useDepartureHistorical from 'redux/packageDepartureHistory';
import { actions } from 'redux/packageDepartureHistory/slice';
import {
  itemsSelector,
  loadedSelector,
  departureTypeSelector,
  destinationOptionsSelector,
  pageableSelector,
} from 'redux/packageDepartureHistory/selectors';
import PackagesList from './components/packagesList';
import ReceiptDownload from './components/receiptDownload';

const idColumn = {
  title: 'Codigo',
  dataIndex: 'code',
  width: 150,
};

const originColumn = {
  title: 'Origen',
  dataIndex: 'originName',
};

const destinationColumn = {
  title: 'Destino',
  dataIndex: 'destinationName',
};

const dispatchTypeColumn = {
  title: 'Tipo de despacho',
  dataIndex: 'dispatchType',
  align: 'center',
};

const dateColumn = {
  title: 'Fecha de despacho',
  dataIndex: 'dateColumn',
  align: 'center',
};

const packagesColumn = {
  title: 'Paquetes',
  align: 'center',
  render: (_, departure) => <PackagesList departure={departure} />,
};

const receiptColumn = {
  title: 'Remito',
  align: 'center',
  render: (_, departure) => <ReceiptDownload departure={departure} />,
};

const dataColumns = [
  idColumn,
  originColumn,
  destinationColumn,
  dispatchTypeColumn,
  dateColumn,
  packagesColumn,
  receiptColumn,
];

const columnsSmallDevice = [...dataColumns];

const columnsMediumDevice = [...dataColumns];

const columnsLargeDevice = [...dataColumns];

const getDepartureTypeName = (departure) =>
  DEPARTURE_TYPE_NAME_OVERRIDES[departure?.value] ?? departure?.name ?? 'Desconocido';

const useDatasource = (loaded) => {
  const items = useSelector(itemsSelector);
  const [datasource, setDatasource] = useState([]);
  const paquerypointsSource = useSelector(paquerypointsObjectItemSelector);
  const logisticOperatorsSource = useSelector(
    logisticOperatorsObjectItemSelector,
  );
  const departureType = useSelector(departureTypeSelector);
  const departureTypes = useSelector(dispatchTypesGlobalsSelector);
  useEffect(() => {
    if (loaded) {
      const paquerypoints = Object.values(paquerypointsSource);
      const logisticOperators = Object.values(logisticOperatorsSource);
      setDatasource(
        items.map((item) => {
          let destinationName = 'Desconocido';
          switch (item.destinationType) {
            case DEPARTURE_TYPE_OPTIONS.LOGISTIC_OPERATOR:
              destinationName =
                logisticOperators.find(
                  (operator) => operator.id === item.destinationId,
                )?.name ?? item.destinationDetail;
              break;
            case DEPARTURE_TYPE_OPTIONS.PAQUERYPOINT:
              destinationName =
                paquerypoints.find(
                  (paquerypoint) => paquerypoint.id === item.destinationId,
                )?.name ?? item.destinationDetail;
              break;
            default:
              break;
          }

          return {
            ...item,
            originName:
              paquerypoints.find(
                (paquerypoint) => paquerypoint.id === item.originId,
              )?.name ?? item.originDetail,
            destinationName,
            dispatchType: getDepartureTypeName(
              departureTypes.find(
                (departure) => departure.value === item.destinationType,
              ),
            ),
            dateColumn: dayjs(item.dispatchedDate)
              .tz()
              .format(TableDateTimeFormat),
          };
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    items,
    paquerypointsSource,
    logisticOperatorsSource,
    loaded,
    departureType,
  ]);
  return datasource;
};

const mapPaquerypointCallback = (paquerypoint) => ({
  name: paquerypoint.name,
  value: paquerypoint.id,
});

const mapDepartureTypeCallback = (departureType) => ({
  name: getDepartureTypeName(departureType),
  value: departureType.value,
});

const useOptions = (items, mapCallback, filterCallback) => {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let itemsArray = Object.values(items);
    if (filterCallback) {
      itemsArray = itemsArray.filter(filterCallback);
    }
    if (itemsArray?.length > 0) {
      setOptions(itemsArray.map(mapCallback));
    }
  }, [items, mapCallback, filterCallback]);
  return options;
};

const filterSelectableDepartureTypes = (departureType) =>
  SELECTABLE_DEPARTURE_TYPE_OPTIONS.includes(departureType.value);

const DeparturePaqueryPointHistory = () => {
  useDepartureHistorical();
  usePaquerypoints({ initialize: true });
  useLogisticOperators({ initialize: true });
  const dispatch = useDispatch();
  const historyLoaded = useSelector(loadedSelector);
  const paquerypointLoaded = useSelector(paquerypointsLoadedSelector);
  const logisticOperatorsLoaded = useSelector(logisticOperatorsLoadedSelector);
  const loaded = historyLoaded && paquerypointLoaded && logisticOperatorsLoaded;
  const pageable = useSelector(pageableSelector);
  const profile = useSelector(authProfile);
  const departureType = useSelector(departureTypeSelector);
  const departureTypes = useSelector(dispatchTypesGlobalsSelector);
  const destinationOptions = useSelector(destinationOptionsSelector);
  const departureTypeOptions = useOptions(
    departureTypes,
    mapDepartureTypeCallback,
    filterSelectableDepartureTypes,
  );
  const paquerypoints = useSelector(paquerypointsObjectItemSelector);
  const paquerypointOptions = useOptions(
    paquerypoints,
    mapPaquerypointCallback,
  );
  const datasource = useDatasource(loaded);

  // El usuario de PaQuery Point despacha siempre desde su propio punto: origen
  // fijo, sin selector. El de OPL opera sobre varios, asi que lo elige.
  const isOpl = isOplRole(profile?.userRoleID);
  useEffect(() => {
    if (!isOpl && profile?.ownerID) {
      dispatch(actions.setOrigin(profile.ownerID));
    }
  }, [isOpl, profile, dispatch]);

  const searchByExternalCodeCallback = useCallback(
    (value) => {
      dispatch(actions.setSearchByExternalCode(value));
    },
    [dispatch],
  );

  const searchByOriginCallback = useCallback(
    (originCriteria) => {
      dispatch(actions.setOrigin(originCriteria));
    },
    [dispatch],
  );

  const searchByDestinationCallback = useCallback(
    (destinationCriteria) => {
      dispatch(actions.setDestination(destinationCriteria));
    },
    [dispatch],
  );

  const updatePaginate = useCallback(
    (page) => {
      dispatch(actions.setPageable(page));
    },
    [dispatch],
  );

  const updateDate = useCallback(
    (value) => {
      dispatch(actions.setDate(value));
    },
    [dispatch],
  );

  const searcher = {
    onSearching: searchByExternalCodeCallback,
    placeholder: 'Código de despacho',
    allowEmptySearch: true,
  };

  const selectDepartureTypeCallback = useCallback(
    (selectedDepartureType) => {
      dispatch(actions.setDepartureType(selectedDepartureType));
    },
    [dispatch],
  );

  const selectors = [
    {
      onChange: selectDepartureTypeCallback,
      placeholder: 'Tipo de despacho',
      list: departureTypeOptions,
    },
    ...(isOpl
      ? [
          {
            onChange: searchByOriginCallback,
            placeholder: 'Origen',
            list: paquerypointOptions,
          },
        ]
      : []),
    {
      key: `destination-${departureType}`,
      onChange: searchByDestinationCallback,
      placeholder: 'Destino',
      list: destinationOptions,
    },
  ];

  const rangePicker = {
    onDateSelection: updateDate,
    required: {
      value: true,
      message: 'La fecha es requerida',
    },
  };

  return (
    <PaqueryTable
      loading={!loaded}
      header={{
        selectors,
        searcher,
        rangePicker,
        refresh: () => dispatch(actions.refreshPage()),
        title: 'Transferir paquetes - Histórico',
      }}
      onChangePaginate={updatePaginate}
      dataSource={datasource}
      paginate={pageable || DEFAULT_PAGINATE}
      dataColumns={dataColumns}
      colsForSmallDevice={columnsSmallDevice}
      colsForMediumDevice={columnsMediumDevice}
      colsForLargeDevice={columnsLargeDevice}
    />
  );
};

export default DeparturePaqueryPointHistory;
